/**
 * SmartVideoPlayer — auto-detects the source type and renders the right
 * embed for it, plus exposes playback progress across every supported
 * source so callers can gate things like a lesson's "Mark complete"
 * button on real watched-seconds instead of just dwell time.
 *
 * Progress callback contract:
 *   `onProgress(playedSeconds: number)` — called ~1×/sec while playing
 *   `onPause()`                          — called when playback pauses/ends
 *
 * Supported inputs (any of these can be pasted in the admin CMS):
 *   - Vimeo URL       → https://vimeo.com/123456789
 *   - Vimeo private   → https://vimeo.com/123456789/abcd1234efg
 *   - Vimeo player    → https://player.vimeo.com/video/123456789
 *   - Bare Vimeo ID   → 123456789
 *   - YouTube URL     → https://www.youtube.com/watch?v=abc123
 *                       https://youtu.be/abc123
 *   - Google Drive    → https://drive.google.com/file/d/FILE_ID/view (must be shared "Anyone with the link")
 *   - Direct MP4/HLS  → https://.../file.mp4  ·  https://.../stream.m3u8
 */
import { useEffect, useMemo, useRef } from "react";
import Player from "@vimeo/player";

// ─── URL detection ──────────────────────────────────────────────────────────

function parseVimeo(url) {
  if (!url) return null;
  const s = String(url).trim();
  if (/^\d{6,}$/.test(s)) return { id: s, hash: null };
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (m) return { id: m[1], hash: m[2] || null };
  return null;
}

function parseYouTube(url) {
  if (!url) return null;
  const s = String(url).trim();
  const patterns = [
    /youtube\.com\/watch\?v=([\w-]+)/,
    /youtu\.be\/([\w-]+)/,
    /youtube\.com\/embed\/([\w-]+)/,
    /youtube\.com\/shorts\/([\w-]+)/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m) return { id: m[1] };
  }
  return null;
}

function parseDrive(url) {
  const s = String(url || "").trim();
  if (!/drive\.google\.com|docs\.google\.com/.test(s)) return null;
  const m = s.match(/\/file\/d\/([\w-]+)/) || s.match(/[?&]id=([\w-]+)/);
  return m ? { id: m[1] } : null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function SmartVideoPlayer({ url, testId, className = "", onProgress, onPause }) {
  const kind = useMemo(() => {
    if (!url) return { type: "empty" };
    const vim = parseVimeo(url);
    if (vim) return { type: "vimeo", ...vim };
    const yt = parseYouTube(url);
    if (yt) return { type: "youtube", ...yt };
    const drive = parseDrive(url);
    if (drive) return { type: "drive", ...drive };
    return { type: "file" };
  }, [url]);

  const iframeRef = useRef(null);

  // Vimeo Player.js hook — official SDK, safest way to get playback
  // seconds across Vimeo's public + unlisted-hash embeds.
  useEffect(() => {
    if (kind.type !== "vimeo" || !iframeRef.current) return;
    let player;
    try {
      player = new Player(iframeRef.current);
    } catch { return; }
    // `timeupdate` fires ~4×/sec while playing. We only forward the
    // absolute `seconds` so the lesson gate can delta-track without
    // caring about paused states.
    const onTime = ({ seconds }) => onProgress?.(seconds);
    const onPauseEvt = () => onPause?.();
    player.on("timeupdate", onTime);
    player.on("pause", onPauseEvt);
    player.on("ended", onPauseEvt);
    return () => {
      try { player.off("timeupdate", onTime); } catch { /* noop */ }
      try { player.off("pause", onPauseEvt); } catch { /* noop */ }
      try { player.off("ended", onPauseEvt); } catch { /* noop */ }
    };
  }, [kind.type, kind.id, onProgress, onPause]);

  // YouTube IFrame Player API via raw postMessage — dodges the extra
  // dependency and works with the `enablejsapi=1` opt-in we set on the
  // src below. We listen for `onStateChange` (1 = playing, 2 = paused,
  // 0 = ended) and separately for `infoDelivery` events that carry the
  // `currentTime` while playing.
  useEffect(() => {
    if (kind.type !== "youtube") return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    // Initialize the listener handshake once the iframe is ready.
    let pollHandle = null;
    const post = (msg) => {
      try { iframe.contentWindow?.postMessage(JSON.stringify(msg), "*"); }
      catch { /* iframe not ready yet */ }
    };
    const kick = () => {
      post({ event: "listening", id: 1, channel: "widget" });
      post({ event: "command", func: "addEventListener", args: ["onStateChange"] });
    };
    // The iframe emits an "onReady" the moment it's live — but if we
    // miss it we'll just retry every 400ms for the first ~3 seconds.
    let attempts = 0;
    pollHandle = setInterval(() => {
      attempts += 1;
      kick();
      if (attempts > 8) clearInterval(pollHandle);
    }, 400);

    // While playing, ask for currentTime every second.
    let tickHandle = null;
    let playing = false;

    const onMessage = (e) => {
      if (!e.data || typeof e.data !== "string") return;
      let payload;
      try { payload = JSON.parse(e.data); } catch { return; }
      // Filter for our iframe's origin — YT posts messages for every
      // player on the page, and we only care about ours.
      if (payload.event === "onStateChange") {
        const state = payload.info;
        if (state === 1) { // PLAYING
          if (!playing) {
            playing = true;
            tickHandle = setInterval(() => {
              post({ event: "command", func: "getCurrentTime", args: [] });
            }, 1000);
          }
        } else if (state === 2 || state === 0) { // PAUSED or ENDED
          playing = false;
          if (tickHandle) { clearInterval(tickHandle); tickHandle = null; }
          onPause?.();
        }
      } else if (payload.event === "infoDelivery" && payload.info?.currentTime !== undefined) {
        onProgress?.(payload.info.currentTime);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (pollHandle) clearInterval(pollHandle);
      if (tickHandle) clearInterval(tickHandle);
    };
  }, [kind.type, kind.id, onProgress, onPause]);

  if (kind.type === "empty") {
    return (
      <div
        data-testid={testId}
        className={`w-full h-full flex items-center justify-center bg-slate-800 text-slate-400 text-sm ${className}`}
      >
        Video coming soon
      </div>
    );
  }

  if (kind.type === "vimeo") {
    // Chrome-less Vimeo embed with sensible defaults for paid education.
    // `api=1` is required for Player.js to hook the iframe.
    const params = "title=0&byline=0&portrait=0&dnt=1&pip=1&playsinline=1&api=1";
    const src = kind.hash
      ? `https://player.vimeo.com/video/${kind.id}?h=${kind.hash}&${params}`
      : `https://player.vimeo.com/video/${kind.id}?${params}`;
    return (
      <iframe
        ref={iframeRef}
        data-testid={testId}
        src={src}
        title="Video player"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        className={`w-full h-full border-0 ${className}`}
      />
    );
  }

  if (kind.type === "youtube") {
    // `enablejsapi=1` opts in to postMessage; `origin` param mitigates
    // an "Unable to post message to *" console warning on some hosts.
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = `rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`;
    return (
      <iframe
        ref={iframeRef}
        data-testid={testId}
        src={`https://www.youtube-nocookie.com/embed/${kind.id}?${params}`}
        title="Video player"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        className={`w-full h-full border-0 ${className}`}
      />
    );
  }

  if (kind.type === "drive") {
    // Drive's preview player exposes no playback events, so the lesson
    // gate falls back to its dwell timer for these.
    return (
      <iframe
        data-testid={testId}
        src={`https://drive.google.com/file/d/${kind.id}/preview`}
        title="Video player"
        allow="autoplay; fullscreen"
        allowFullScreen
        className={`w-full h-full border-0 ${className}`}
      />
    );
  }

  // Fallback: assume it's a directly-playable file (mp4, webm, m3u8).
  // Wire `onTimeUpdate` (fires 3-4×/sec while playing) into the caller's
  // `onProgress` callback so the lesson player can gate its "Mark
  // complete" button on actual playback for native files too.
  return (
    <video
      data-testid={testId}
      controls
      controlsList="nodownload"
      className={`w-full h-full ${className}`}
      src={url}
      onTimeUpdate={(e) => onProgress?.(e.currentTarget.currentTime)}
      onPause={onPause}
    />
  );
}
